import React, { useCallback, useState } from 'react';

import { Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Typography,
  TextField,
  Button,
  Dialog,
  IconButton,
} from '@mui/material';

import { flagOptions } from 'helpers';
import { useSnackContext } from 'hooks/useSnackContext';
import { FlagType } from 'types';

import {
  ButtonWrapper,
  ContentWrapper,
  CloseWrapper,
} from './styledComponents';

export interface AddFlagProps {
  open: boolean;
  candidateIds: Array<number>;
  handleAddFlags: (
    candidateIds: Array<number>,
    flagType: FlagType,
    message: string,
  ) => void;
  handleClose: () => void;
}
export const AddFlag: React.FC<AddFlagProps> = (props) => {
  const { open, candidateIds, handleAddFlags, handleClose } = props;
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState({
    noteError: false,
    missingFlagError: false,
  });
  const [flagType, setFlagType] = useState<FlagType | null>(null);

  const checkedCandidateCount = candidateIds.length;

  const { setSnack } = useSnackContext();

  const addFlags = useCallback(
    async (e: React.FormEvent<HTMLButtonElement>) => {
      e.preventDefault();
      try {
        if (
          !flagType ||
          (['unable_to_verify', 'admin_flag'].includes(flagType) && !note)
        ) {
          return setErrorState({
            noteError:
              flagType && !note
                ? ['unable_to_verify', 'admin_flag'].includes(flagType)
                : false,
            missingFlagError: !flagType,
          });
        }
        setLoading(true);
        await handleAddFlags(candidateIds, flagType, note);
        handleClose();
      } catch (e) {
        setSnack((props) => ({
          ...props,
          severity: 'error',
          open: true,
          message: 'Failed To Add Flag',
          error: e as Error,
        }));
      } finally {
        setLoading(false);
        setNote('');
      }
    },
    [flagType, handleClose, note, setSnack, candidateIds, handleAddFlags],
  );

  return (
    <Dialog open={open}>
      <CloseWrapper>
        <IconButton
          sx={{ alignSelf: 'right' }}
          aria-label='close modal'
          component='span'
          onClick={handleClose}
        >
          <Close />
        </IconButton>
      </CloseWrapper>
      <ContentWrapper>
        <Typography variant='h5' sx={{ fontWeight: 'bold' }}>
          {`You are adding a flag to ${checkedCandidateCount} ${
            checkedCandidateCount === 1 ? 'Candidate' : 'Candidates'
          }`}{' '}
        </Typography>
        <Typography variant='h5' sx={{ mt: 2 }}>
          Additional Information
        </Typography>
        <Typography variant='body1'>
          Select a flag and include any relevant details in the note section
        </Typography>
        <FormControl sx={{ mt: 2 }}>
          <RadioGroup
            onChange={(e, value) => {
              e.preventDefault();
              setFlagType(value as FlagType);
            }}
          >
            {flagOptions.map((flag) => (
              <FormControlLabel
                key={flag.value}
                value={flag.value}
                control={<Radio />}
                label={
                  ['unable_to_verify', 'admin_flag'].includes(flag.value)
                    ? `${flag.label} (note required)`
                    : flag.label
                }
              />
            ))}
          </RadioGroup>
          <Typography sx={{ mt: 1 }} variant='h5'>
            Add Note
          </Typography>
          <TextField
            multiline
            onChange={(e) => {
              const { value } = e.currentTarget;
              setNote(value);
            }}
            value={note}
            error={errorState.noteError}
            helperText={errorState.noteError && 'You must include a note'}
          />
          <ButtonWrapper>
            <Button
              sx={{ mt: 2, mr: 1 }}
              onClick={handleClose}
              variant='outlined'
            >
              CANCEL
            </Button>
            <LoadingButton
              sx={{ mt: 2, ml: 1 }}
              disabled={!flagType}
              loading={loading}
              onClick={addFlags}
              variant='contained'
            >
              {checkedCandidateCount === 1 ? 'ADD FLAG' : 'ADD FLAGS'}
            </LoadingButton>
          </ButtonWrapper>
        </FormControl>
      </ContentWrapper>
    </Dialog>
  );
};
